import { h } from '@atomify/jsx';
import { MapTheme, PointerTheme, PolygonTheme, RGBA } from '@bpd-library/types';
import { MapLabel } from '@source/components/atoms/map-label';
import { PickPartial } from '@utilities/types';

export type MapThemeExtension = {
    polygon?: PolygonTheme;
    pointer?: PickPartial<PointerTheme, 'settings'>;
};

export type RGB = [number, number, number];

export const WHITE: RGB = [255, 255, 255]; // #FFFFFF
export const GREY_LIGHT: RGB = [190, 190, 190]; // #BEBEBE
export const GREY_DARK: RGB = [77, 77, 77]; // #4D4D4D
export const PURPLE: RGB = [217, 63, 115]; // #D93F73
export const RED: RGB = [200, 56, 53]; // #C83835
export const ORANGE: RGB = [240, 164, 0]; // #F0A400
export const GREEN: RGB = [176, 209, 72]; // #B0D148
export const BLUE: RGB = [50, 193, 222]; // #32C1DE

export const WHITE_ALPHA = [...WHITE, 0.25] as RGBA;
export const GREY_LIGHT_ALPHA = [...GREY_LIGHT, 0.25] as RGBA;
export const GREY_DARK_ALPHA = [...GREY_DARK, 0.25] as RGBA;
export const PURPLE_ALPHA = [...PURPLE, 0.4] as RGBA;
export const PURPLE_ALPHA_LIGHT = [...PURPLE, 0.7] as RGBA;
export const RED_ALPHA = [...RED, 0.6] as RGBA;
export const ORANGE_ALPHA = [...ORANGE, 0.4] as RGBA;
export const GREEN_ALPHA = [...GREEN, 0.5] as RGBA;
export const BLUE_ALPHA = [...BLUE, 0.6] as RGBA;
export const BLUE_ALPHA_LIGHT = [...BLUE, 0.25] as RGBA;

export const HOVER: {
    fill: RGBA;
    stroke: RGBA;
} = {
    fill: BLUE_ALPHA,
    stroke: [...WHITE, 0.8],
};

export const POLYGON_STATUS_THEME: MapThemeExtension = {
    polygon: {
        settings: [
            {
                variant: 'inPreparation',
                normal: {
                    fill: WHITE_ALPHA,
                    stroke: [...GREY_LIGHT, 1],
                },
                hover: HOVER,
            },
            {
                variant: 'available',
                normal: {
                    fill: GREEN_ALPHA,
                    stroke: [...WHITE, 1],
                },
                hover: HOVER,
            },
            {
                variant: 'inOption',
                normal: {
                    fill: ORANGE_ALPHA,
                    stroke: [...WHITE, 1],
                },
                hover: HOVER,
            },
            {
                variant: 'reserved',
                normal: {
                    fill: ORANGE_ALPHA,
                    stroke: [...WHITE, 1],
                },
                hover: HOVER,
            },
            {
                variant: 'sold',
                normal: {
                    fill: RED_ALPHA,
                    stroke: [...WHITE, 1],
                },
                hover: HOVER,
            },
            {
                variant: 'underConstruction',
                normal: {
                    fill: PURPLE_ALPHA,
                    stroke: [...WHITE, 1],
                },
                hover: HOVER,
            },
        ],
    },
};

export const PROJECT_PHASE_THEME: MapThemeExtension = {
    polygon: {
        settings: [
            {
                variant: 'projectPhase',
                normal: {
                    fill: WHITE_ALPHA,
                    stroke: [...GREY_DARK, 1],
                },
                hover: {
                    fill: WHITE_ALPHA,
                    stroke: [...GREY_DARK, 1],
                },
            },
        ],
    },
    pointer: {
        settings: [
            {
                variant: 'projectPhasePointer',
                minzoom: 14,
                createElement: (item) => {
                    return <MapLabel title={item.title} subtitle={item.status} />;
                },
            },
        ],
    },
};

export const extendMapTheme = (...mapThemes: (MapTheme | MapThemeExtension)[]): MapTheme => {
    const newTheme: MapTheme = {
        polygon: { settings: [] },
        pointer: { settings: [], cluster: (total) => <span>{total}</span> },
    };

    mapThemes.forEach(({ polygon, pointer }) => {
        polygon?.settings.length && newTheme.polygon?.settings?.push(...polygon.settings);
        pointer?.settings.length && newTheme.pointer?.settings?.push(...pointer.settings);
        if (pointer?.cluster && newTheme.pointer) {
            newTheme.pointer.cluster = pointer.cluster;
        }
    });

    return newTheme;
};

export const THEME = extendMapTheme(POLYGON_STATUS_THEME, PROJECT_PHASE_THEME);
